import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

interface WeightageScoreColumn {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  visible?: boolean;
}

interface WeightageScoreRow {
  factor: string;
  comments: string;
  weight: string;
  tone?: string;
}

interface WeightageScoreSection {
  groupLabel: string;
  axisLabel: string;
  axisTitle: string;
  rows: WeightageScoreRow[];
  metaBackground?: string;
  groupBackground?: string;
  groupColor?: string;
}

interface WeightageScoreFlatRow {
  section: WeightageScoreSection;
  row: WeightageScoreRow;
  rowIndex: number;
  rowSpan: number;
  showSectionMeta: boolean;
}

@Component({
  selector: 'app-weightage-score-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './weightage-score-table.component.html',
  styleUrls: ['./weightage-score-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeightageScoreTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;

  flatRows: WeightageScoreFlatRow[] = [];
  loading = false;
  errorMessage = '';
  private loadSub?: Subscription;

  constructor(
    private readonly configService: ConfigService,
    private readonly globalFilterService: GlobalFilterService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnChanges(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }

  get title(): string {
    return String(this.data?.title || '');
  }

  get subtitle(): string {
    return String(this.data?.subtitle || '');
  }

  get sourceNote(): string {
    return String(this.data?.sourceNote || '');
  }

  get visibleColumns(): WeightageScoreColumn[] {
    const columns = Array.isArray(this.data?.table?.columns) ? this.data.table.columns : [];
    return columns.filter((column: WeightageScoreColumn) => column?.visible !== false);
  }

  get sections(): WeightageScoreSection[] {
    return Array.isArray(this.data?.sections) ? this.data.sections : [];
  }

  get showTable(): boolean {
    return !this.loading && !this.errorMessage && this.flatRows.length > 0;
  }

  get fillHeightEnabled(): boolean {
    return this.data?.fillHeight === true || this.data?.table?.fillHeight === true;
  }

  get headerStyle(): Record<string, string> {
    return {
      background: String(this.data?.table?.headerBackground || '#eef3f9'),
      color: String(this.data?.table?.headerColor || '#202124'),
    };
  }

  get tableStyle(): Record<string, string> {
    return {
      '--wst-grid-line': String(this.data?.theme?.gridLine || '#eadfda'),
      '--wst-dashed-line': String(this.data?.theme?.dashedLine || '#aeb5bf'),
      '--wst-surface': String(this.data?.theme?.surface || '#ffffff'),
      '--wst-shell': String(this.data?.theme?.shell || '#fbfaf9'),
      '--wst-group-bg': String(this.data?.theme?.groupBackground || '#434343'),
      '--wst-group-color': String(this.data?.theme?.groupColor || '#ffffff'),
      '--wst-axis-accent': String(this.data?.theme?.axisAccent || '#c5161d'),
      '--wst-meta-bg': String(this.data?.theme?.metaBackground || '#f1f1f1'),
      '--wst-text': String(this.data?.theme?.text || '#2d2d2d'),
      '--wst-muted': String(this.data?.theme?.muted || '#555b66'),
    };
  }

  get shellStyle(): Record<string, string> {
    const configuredHeight = String(
      this.data?.height ??
      this.data?.table?.height ??
      this.data?.table?.containerHeight ??
      '',
    ).trim();
    const configuredMinHeight = String(this.data?.table?.minHeight ?? '').trim();
    const configuredMaxHeight = String(this.data?.table?.maxHeight ?? '').trim();
    const configuredHeaderHeight = String(this.data?.table?.headerHeight ?? '66px').trim();
    const fillHeight = this.fillHeightEnabled;

    return {
      height: fillHeight ? (configuredHeight || '') : '',
      minHeight: configuredMinHeight || '',
      maxHeight: configuredMaxHeight || (fillHeight ? configuredHeight || '' : ''),
      '--wst-header-height': configuredHeaderHeight || '66px',
      '--wst-row-count': String(Math.max(this.flatRows.length, 1)),
      '--wst-cell-padding-y': '16px',
    };
  }

  getSectionMetaStyle(section: WeightageScoreSection): Record<string, string> {
    return {
      background: String(section?.['metaBackground'] || this.data?.theme?.metaBackground || '#f1f1f1'),
    };
  }

  getGroupCellStyle(section: WeightageScoreSection): Record<string, string> {
    return {
      background: String(section?.['groupBackground'] || this.data?.theme?.groupBackground || '#434343'),
      color: String(section?.['groupColor'] || this.data?.theme?.groupColor || '#ffffff'),
    };
  }

  getRowToneStyle(row: WeightageScoreRow): Record<string, string> {
    const toneKey = String(row?.tone || '').trim();
    const tones = this.data?.tones || {};
    const tone = toneKey ? tones?.[toneKey] : null;

    return {
      background: String(tone?.background || '#ffffff'),
    };
  }

  getColumnStyle(column: WeightageScoreColumn): Record<string, string> {
    return {
      width: column?.width || '',
      textAlign: column?.align || 'left',
    };
  }

  getCellValue(row: WeightageScoreRow, column: WeightageScoreColumn): string {
    const value = row?.[column.key as keyof WeightageScoreRow];
    return typeof value === 'string' ? value : '';
  }

  trackByRow = (_: number, item: WeightageScoreFlatRow): string =>
    `${item.section.groupLabel}-${item.section.axisLabel}-${item.row.factor}-${item.rowIndex}`;

  private initializeComponent(): void {
    this.loadSub?.unsubscribe();
    this.errorMessage = '';

    if (this.shouldLoadFromApi()) {
      this.loadFromApi();
      return;
    }

    this.flatRows = this.buildFlatRows(this.sections);
    this.cdr.markForCheck();
  }

  private shouldLoadFromApi(): boolean {
    return !!this.data?.apiUrl && !!this.data?.functionName;
  }

  private loadFromApi(): void {
    this.loading = true;
    this.flatRows = [];

    this.loadSub = this.configService
      .getConfig(
        this.data.apiUrl,
        this.data,
        {
          ...(this.globalFilterService.getCurrentFilters() || {}),
          useQueryParams: this.data?.useQueryParams || false,
        },
        this.data?.method || 'post',
      )
      .subscribe({
        next: (response: any) => {
          const mappedSections = this.mapApiSections(response);
          this.flatRows = this.buildFlatRows(mappedSections.length ? mappedSections : this.sections);
          this.loading = false;
          this.errorMessage = '';
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.errorMessage = String(this.data?.messages?.error || '');
          this.flatRows = this.buildFlatRows(this.sections);
          this.cdr.markForCheck();
        },
      });
  }

  private mapApiSections(response: any): WeightageScoreSection[] {
    const configuredSectionsPath = String(this.data?.responseMapping?.sectionsPath || '').trim();
    const configuredRowsPath = String(this.data?.responseMapping?.rowsPath || '').trim();

    const directSections = configuredSectionsPath ? this.getValueByPath(response, configuredSectionsPath) : undefined;
    if (Array.isArray(directSections)) {
      return this.normalizeSections(directSections);
    }

    const directRows = configuredRowsPath ? this.getValueByPath(response, configuredRowsPath) : undefined;
    if (Array.isArray(directRows)) {
      return this.mapRowsIntoConfiguredSections(directRows);
    }

    return [];
  }

  private normalizeSections(sections: any[]): WeightageScoreSection[] {
    return sections
      .map((section: any) => ({
        groupLabel: String(section?.groupLabel || section?.group_label || ''),
        axisLabel: String(section?.axisLabel || section?.axis_label || ''),
        axisTitle: String(section?.axisTitle || section?.axis_title || ''),
        metaBackground: section?.metaBackground,
        groupBackground: section?.groupBackground,
        groupColor: section?.groupColor,
        rows: Array.isArray(section?.rows)
          ? section.rows.map((row: any) => ({
              factor: String(row?.factor || ''),
              comments: String(row?.comments || ''),
              weight: String(row?.weight || ''),
              tone: row?.tone ? String(row.tone) : undefined,
            }))
          : [],
      }))
      .filter((section: WeightageScoreSection) => !!section.groupLabel && section.rows.length > 0);
  }

  private mapRowsIntoConfiguredSections(rows: any[]): WeightageScoreSection[] {
    const mapping = this.data?.responseMapping || {};
    const fieldMap = mapping?.rowFieldMap || {};
    const sectionMap = mapping?.sectionMap || {};

    return this.sections
      .map((section: WeightageScoreSection) => {
        const normalizedGroup = this.normalizeLookup(section.groupLabel);
        const normalizedAxis = this.normalizeLookup(section.axisLabel);
        const filteredRows = rows.filter((row: any) => {
          const groupValue = this.normalizeLookup(row?.[sectionMap?.groupKey || 'groupLabel']);
          const axisValue = this.normalizeLookup(row?.[sectionMap?.axisKey || 'axisLabel']);
          return groupValue === normalizedGroup && axisValue === normalizedAxis;
        });

        return {
          ...section,
          rows: filteredRows.map((row: any) => ({
            factor: String(row?.[fieldMap?.factor || 'factor'] || ''),
            comments: String(row?.[fieldMap?.comments || 'comments'] || ''),
            weight: String(row?.[fieldMap?.weight || 'weight'] || ''),
            tone: row?.[fieldMap?.tone || 'tone'] ? String(row[fieldMap?.tone || 'tone']) : undefined,
          })),
        };
      })
      .filter((section: WeightageScoreSection) => section.rows.length > 0);
  }

  private getValueByPath(source: any, path: string): any {
    return path.split('.').reduce((acc: any, part: string) => {
      if (acc == null) {
        return undefined;
      }

      if (/^\d+$/.test(part)) {
        return acc[Number(part)];
      }

      return acc[part];
    }, source);
  }

  private normalizeLookup(value: any): string {
    return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private buildFlatRows(sections: WeightageScoreSection[]): WeightageScoreFlatRow[] {
    return sections.flatMap((section: WeightageScoreSection) => {
      const rows = Array.isArray(section?.rows) ? section.rows : [];
      return rows.map((row, rowIndex) => ({
        section,
        row,
        rowIndex,
        rowSpan: rows.length || 1,
        showSectionMeta: rowIndex === 0,
      }));
    });
  }
}
